import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import FormContentManager from "../../components/common/form/FormContentManager";
import TableCell from "../../components/common/ui/TableCell";
import * as designMeta from "../../api/design/meta";
import DesignTreePicker from "../../components/design/DesignTreePicker";
import TinyMCEeditor from "../../components/const/TinyMCEeditor";
import PeopleCellLoader from "../../components/common/people/PeopleCellLoader";
import { getDocPersons } from "../../api/papers";

export default function Dcn_new() {
  const { id: projectId, dns: paramDns } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const reasonEditorRef = useRef(null);
  const me = JSON.parse(localStorage.getItem("user"));
  const myDeptFull = me ? `${me.company}(${me.department})` : "";

  const [requestPeople, setRequestPeople] = useState([]);

  const [form, setForm] = useState({
    title: "",
    issueDept: myDeptFull,
    requestDept: myDeptFull,
    reason: "",
    details: [
      { id: 1, drawingNo: "", version: "", changeDesc: "", file: null },
    ],
  });

  /* ---------------------------------------------------
         수정 모드: 기존 meta 로드
      --------------------------------------------------- */
  useEffect(() => {
    if (!paramDns) return; // new 모드

    const load = async () => {
      try {
        const res = await designMeta.getMeta({ dns: paramDns });
        const meta = res.meta || res.data?.meta || {};

        const loadedPeople = await getDocPersons(paramDns);

        setDns(paramDns);

        setRequestPeople(loadedPeople);

        setForm({
          title: meta.title || "",
          requestDept: meta.requestDept || "",
          reason: meta.reason || "",
          attachments: meta.attachments || [],
          details: meta.details || [],
          alert: meta.alert || {
            send: true,
            urgent: false,
            departments: (loadedPeople || []).map((p) => p.id),
          },
        });
      } catch (e) {
        console.error(e);
        alert("기존 문서를 불러오지 못했습니다.");
      }
    };

    load();
  }, [paramDns]);

  const [dns, setDns] = useState(paramDns || "");
  const [linkedFcn, setLinkedFcn] = useState(null);
  const [fcnList, setFcnList] = useState([]);
  const [showDesignPicker, setShowDesignPicker] = useState(false);
  const [activeRow, setActiveRow] = useState(null);

  const getBody = useCallback(() => {
    const reasonHtml = reasonEditorRef.current?.getContent() || "";

    const validRequestPeople = requestPeople.filter(
      (p) => p && (p.user || p.id)
    );

    return {
      values: {
        project: projectId,
        module: "design",
        doc_type: "dcn",
        dns,
        related_dns: linkedFcn?.doc_dns || null,

        title: form.title,

        meta: {
          title: form.title,
          issueDept: form.issueDept,
          requestDept: form.requestDept,
          requestDeptList: form.requestDeptList || [],
          reason: reasonHtml || form.reason,

          requestPeople: validRequestPeople.map((p) => p.id),

          details: form.details.map((d, idx) => ({
            id: d.id || idx + 1,
            drawingNo: d.drawingNo || "",
            version: d.version || "",
            changeDesc: d.changeDesc || "",
            drawingFileId: d.drawingFileId || null,
            drawingFileUrl: d.drawingFileUrl || "",
            file: d.file || null,
          })),

          linkedFcn: linkedFcn?.doc_dns || null,
          attachments: form.attachments || [],

          alert: {
            send: true,
            urgent: false,
            departments: validRequestPeople.map((p) => p.user?.id || p.id),
          },
        },
      },
    };
  }, [form, dns, linkedFcn, projectId, requestPeople]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fcn_dns = params.get("fromFCN");
    if (fcn_dns) loadFcn(fcn_dns);
  }, [location.search]);

  useEffect(() => {
    const fetchFcnList = async () => {
      try {
        const res = await designMeta.getMetaMyList({
          project: projectId,
          doc_type: "FCN",
        });
        setFcnList(Array.isArray(res.results) ? res.results : []);
      } catch (err) {
        console.error("FCN 목록 불러오기 실패:", err);
      }
    };
    fetchFcnList();
  }, [projectId]);

  const loadFcn = async (fcn_dns) => {
    try {
      const res = await designMeta.getMeta({ dns: fcn_dns });
      const fcn = res.data || res;
      setLinkedFcn(fcn);

      const meta = fcn.meta || {};

      setForm((prev) => ({
        ...prev,

        title: meta.title ? `${meta.title} 변경도면` : "설계변경도면",

        requestDept: meta.requestDept || "",
        requestDeptList: meta.requestDeptList || [],

        reason: meta.reason || "FCN 통보에 따른 변경도면 발행.",

        details: (meta.details || []).map((d, i) => ({
          id: i + 1,
          drawingNo: d.item || d.drawingNo || "",
          drawingFileId: d.drawingFileId || null,
          drawingFileUrl: d.drawingFileUrl || "",
          version: "",
          changeDesc: `${d.after || "-"} 변경 반영`,
          file: null,
        })),
      }));
    } catch (err) {
      console.error("FCN 불러오기 실패:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDetailChange = (index, key, value) => {
    setForm((prev) => {
      const updated = [...prev.details];
      updated[index][key] = value;
      return { ...prev, details: updated };
    });
  };

  const addDetailRow = () =>
    setForm((prev) => ({
      ...prev,
      details: [
        ...prev.details,
        {
          id: prev.details.length + 1,
          drawingNo: "",
          version: "",
          changeDesc: "",
          file: null,
        },
      ],
    }));

  const removeDetailRow = (i) =>
    setForm((prev) => ({
      ...prev,
      details: prev.details.filter((_, idx) => idx !== i),
    }));

  return (
    <FormContentManager
      params={{
        module: "design",
        doc_type: "dcn",
        project: projectId.trim(),
        dns,
        related_dns: linkedFcn?.doc_dns || null,
      }}
      getBody={getBody}
      onChangeId={(newDns) => setDns(newDns)}
      redirectTo={`/project/${projectId}/design/list`}
      docName={"설계변경도면 (DCN)"}
      docDescribe={"도면별 변경내용과 파일을 첨부하여 발행하세요."}
      reviewer={requestPeople}
      summaryConfig={{
        title: "도면 발행 요약",
        sections: [
          { label: "제목", value: form.title },
          // { label: "발행부서", value: form.issueDept },
          {
            label: "검토 담당자",
            value:
              requestPeople.map((r) => r.user?.name || r.name).join(", ") ||
              "-",
          },
        ],
        list: [
          {
            label: "변경 도면",
            items: form.details?.map(
              (d) => `${d.drawingNo || "-"}: ${d.changeDesc || "-"}`
            ),
          },
        ],
      }}
    >
      <div className="">
        <div className="mb-4 flex items-center gap-2">
          <label className="font-medium text-sm">연결 문서</label>
          {linkedFcn ? (
            <span className="text-sm">{linkedFcn.doc_dns}</span>
          ) : (
            <select
              className="border rounded px-2 py-1 text-sm"
              onChange={(e) => loadFcn(e.target.value)}
            >
              <option value="">선택하세요</option>
              {fcnList.map((fcn) => (
                <option key={fcn.doc_dns} value={fcn.doc_dns}>
                  {fcn.doc_dns} - {fcn.title}
                </option>
              ))}
            </select>
          )}
        </div>

        <table className="w-full border text-sm mb-6 table-fixed">
          <colgroup>
            <col style={{ width: "100px" }} />
            <col style={{ width: "70%" }} />
            <col style={{ width: "80px" }} />
            <col style={{ width: "40%" }} />
          </colgroup>
          <tbody >
            <tr >
              <TableCell
                label="제목"
                input={
                  <input
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    className="w-full border rounded px-2 py-1"
                    placeholder="예: 구조 보강 상세 변경 도면"
                  />
                }
                editing
                colSpan={3}
                labelClass="px-3 py-1.5 text-[13px]"
                valueClass="px-3 py-1.5 text-[13px]"
              />
            </tr>
            {/* <tr >
              <TableCell
                label="발행 부서"
                value={form.issueDept || "-"}
                labelClass="px-3 py-1.5 text-[13px]"
                valueClass="px-3 py-1.5 text-[13px]"
              />
              </tr> */}
            <tr >
              <TableCell
                label="CM 검토 담당자"
                colSpan={3}
                editing
                labelClass="bg-gray-50 px-3 py-1.5 text-[13px]"
                valueClass="px-3 py-1.5 text-[13px]"
                input={
                  <PeopleCellLoader
                    single
                    dns={paramDns}
                    value={requestPeople}
                    onChange={(list) => setRequestPeople(list)}
                  />
                }
              />
            </tr>
            <tr >
              <TableCell
                label="변경 사유"
                input={
                  <TinyMCEeditor
                    height={300}
                    initialValue={form.reason}
                    editorRef={reasonEditorRef}
                  />
                }
                editing
                colSpan={3}
                labelClass="px-3 py-1.5 align-top"
                valueClass="px-3 py-1.5 align-top"
              />
            </tr>
          </tbody>
        </table>

        <h2 className="text-body-bold mb-2">변경 도면 목록</h2>
        <div className="border-t border-r border-l border-black">
          <table className="w-full border-collapse text-body">
            <thead className="doc-thead">
              <tr>
                <th className="doc-th w-12 text-center">No</th>
                <th className="doc-th ">도면번호</th>
                <th className="doc-th ">버전</th>
                <th className="doc-th ">변경 내용</th>
                <th className="doc-th w-40 text-center">
                  도면 파일
                </th>
                <th className="doc-th-end text-center w-14">관리</th>
              </tr>
            </thead>
            <tbody >
              {form.details.map((row, i) => (
                <tr key={row.id} className="border-b border-black">
                  <td className="text-center px-3 py-1.5 border-r border-black">{i + 1}</td>
                  <td className="border-r px-3 py-1.5 border-black">
                    <div className="flex items-center gap-1">
                      {row.drawingFileId ? (
                        <div className="flex items-center justify-between w-full border rounded px-2 py-1 bg-slate-50">
                          <span className="truncate max-w-[150px]">
                            {row.drawingNo}
                          </span>
                          <button
                            onClick={() => {
                              handleDetailChange(i, "drawingFileId", null);
                              handleDetailChange(i, "drawingFileUrl", "");
                              handleDetailChange(i, "drawingNo", "");
                            }}
                            className="ml-2 text-red-500 hover:text-red-600 text-xs"
                            title="선택 해제"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="text"
                            value={row.drawingNo}
                            onChange={(e) =>
                              handleDetailChange(i, "drawingNo", e.target.value)
                            }
                            className="w-full border rounded px-2 py-1 text-[13px]"
                            placeholder="도면번호 또는 선택"
                          />
                          <button
                            onClick={() => {
                              setActiveRow(i);
                              setShowDesignPicker(true);
                            }}
                            className="px-2 py-1 text-xs border rounded hover:bg-gray-100"
                            title="설계도서에서 선택"
                          >
                            📂
                          </button>
                        </>
                      )}
                    </div>
                  </td>

                  <td className="border-r px-3 py-1.5 border-black">
                    <input
                      type="text"
                      value={row.version}
                      onChange={(e) =>
                        handleDetailChange(i, "version", e.target.value)
                      }
                      className="w-full border rounded px-2 py-1"
                    />
                  </td>
                  <td className="border-r px-3 py-1.5 border-black">
                    <input
                      type="text"
                      value={row.changeDesc}
                      onChange={(e) =>
                        handleDetailChange(i, "changeDesc", e.target.value)
                      }
                      className="w-full border rounded px-2 py-1"
                    />
                  </td>

                  <td className="text-center px-2 border-r border-black">
                    {!row.file ? (
                      <label className="cursor-pointer text-[12px] px-3 py-1 border rounded-md bg-gray-50 hover:bg-indigo-50 transition text-gray-700 inline-block">
                        파일 선택
                        <input
                          type="file"
                          onChange={(e) =>
                            handleDetailChange(
                              i,
                              "file",
                              e.target.files[0] || null
                            )
                          }
                          className="hidden"
                        />
                      </label>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-[12px] text-gray-600 truncate max-w-[110px]">
                          {row.file.name}
                        </span>
                        <button
                          onClick={() => handleDetailChange(i, "file", null)}
                          className="text-red-500 text-[12px] hover:text-red-600"
                          title="파일 삭제"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </td>

                  <td className="text-center px-3 py-1.5">
                    <button
                      onClick={() => removeDetailRow(i)}
                      className="doc-del"
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-right mt-2">
          <button onClick={addDetailRow} className="btn-basic-sm">
            + 행 추가
          </button>
        </div>

        {linkedFcn && (
          <div className="mt-4 text-sm text-slate-600">
            ※ 본 문서는{" "}
            <a
              href={`/project/${projectId}/design/fcn/${linkedFcn.doc_dns}`}
              className="text-indigo-600 underline hover:text-indigo-800"
              target="_blank"
              rel="noopener noreferrer"
            >
              FCN ({linkedFcn.doc_dns})
            </a>{" "}
            통보서를 기반으로 작성되었습니다.
          </div>
        )}
      </div>
      {showDesignPicker && (
        <DesignTreePicker
          open={showDesignPicker}
          onClose={() => setShowDesignPicker(false)}
          onConfirm={(file) => {
            handleDetailChange(activeRow, "drawingNo", file.name || file.title);
            handleDetailChange(activeRow, "drawingFileId", file.id);
            handleDetailChange(
              activeRow,
              "drawingFileUrl",
              file.file_url || file.url || ""
            );
          }}
        />
      )}
    </FormContentManager>
  );
}
